import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Bookmark, X } from 'lucide-react';
import { Api } from '../../lib/api';
import { useUi } from '../../store/ui';
import { useTypes } from '../../hooks/useVault';
import { sanitizeSnippet } from '../links/BacklinksPanel';
import { TypeDot } from '../ui/TypeDot';

/** Full search view (scope §6.4): facets, result list with "why", preview pane, saved searches. */
export function SearchView() {
  const q = useUi((s) => s.searchQuery);
  const mode = useUi((s) => s.searchMode);
  const setSearch = useUi((s) => s.setSearch);
  const openNote = useUi((s) => s.openNote);
  const toast = useUi((s) => s.toast);
  const [text, setText] = useState(q);
  const [type, setType] = useState<string>('');
  const [tag, setTag] = useState<string>('');
  const [folder, setFolder] = useState<string>('');
  const [sel, setSel] = useState(0);
  const types = useTypes();
  const qc = useQueryClient();
  useEffect(() => setText(q), [q]);

  const debounced = useDebounced(text, 120);
  const results = useQuery({
    queryKey: ['search-view', debounced, mode, type, tag, folder],
    queryFn: () => Api.search(debounced, { limit: 50, mode, type: type || undefined, tag: tag || undefined, folder: folder || undefined }),
    enabled: debounced.trim().length > 0,
    placeholderData: keepPreviousData,
  });
  const facets = useQuery({ queryKey: ['search-facets', debounced], queryFn: () => Api.searchFacets(debounced), enabled: debounced.trim().length > 0, placeholderData: keepPreviousData });
  const status = useQuery({ queryKey: ['search-status'], queryFn: Api.searchStatus, refetchInterval: 15_000 });
  const current = results.data?.hits[sel];
  const preview = useQuery({ queryKey: ['note', current?.id], queryFn: () => Api.notes.get(current!.id), enabled: !!current, staleTime: 5_000 });
  const typeLabel = useMemo(() => (t: string) => types.data?.find((x) => x.key === t)?.label ?? t, [types.data]);

  const save = async () => {
    const name = window.prompt('Name this search:', text.slice(0, 60));
    if (!name) return;
    await Api.savedSearches.save({ name, query: text, mode, type: type || undefined, tag: tag || undefined, folder: folder || undefined });
    qc.invalidateQueries({ queryKey: ['saved-searches'] });
    toast(`Saved search "${name}" (.cortex/searches/)`);
  };

  return (
    <div className="search-view">
      <div className="search-bar">
        <input
          type="search"
          value={text}
          autoFocus
          placeholder='Search the vault…   type:device site:"South Walker" #tag -excluded "exact phrase"'
          onChange={(e) => { setText(e.target.value); setSel(0); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min((results.data?.hits.length ?? 1) - 1, s + 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
            if (e.key === 'Enter' && current) openNote(current.id, { split: e.ctrlKey || e.metaKey });
          }}
        />
        <div className="modes">
          <button className={mode === 'hybrid' ? 'on' : ''} onClick={() => setSearch(text, 'hybrid')} title="Keyword + meaning + links">Hybrid</button>
          <button className={mode === 'keyword' ? 'on' : ''} onClick={() => setSearch(text, 'keyword')} title="FTS only">Keyword</button>
        </div>
        <button className="ghost" onClick={() => void save()} disabled={!text.trim()} title="Pin to the sidebar as a file in .cortex/searches"><Bookmark size={13} /> Save</button>
      </div>
      {results.data?.degraded && (
        <div className="pane-banner"><span className="light warn" /> Hybrid unavailable: {results.data.degraded}</div>
      )}
      {status.data && !status.data.hybrid.ok && !results.data?.degraded && mode === 'hybrid' && (
        <div className="pane-banner"><span className="light warn" /> {status.data.hybrid.reason}</div>
      )}
      <div className="search-body">
        <aside className="search-facets">
          <Facet title="Type" values={facets.data?.type ?? {}} selected={type} onSelect={setType} label={typeLabel} />
          <Facet title="Tag" values={facets.data?.tag ?? {}} selected={tag} onSelect={setTag} label={(t) => `#${t}`} />
          <Facet title="Folder" values={facets.data?.folder ?? {}} selected={folder} onSelect={setFolder} label={(f) => f} />
          {status.data?.embed && (
            <div className="facet">
              <div className="label">Embeddings</div>
              <div className="mono faint" style={{ lineHeight: 1.6 }}>
                {status.data.embed.embedder ?? 'none'}<br />
                {status.data.embed.done}/{status.data.embed.total} chunks{status.data.embed.pending ? ` · ${status.data.embed.pending} pending` : ''}<br />
                <span className={`light ${status.data.embed.healthy ? 'ok' : 'warn'}`} /> {status.data.embed.healthy ? 'embedder healthy' : status.data.embed.healthDetail}
                {status.data.localOnly && <><br /><span className="chip ok">local only</span></>}
              </div>
            </div>
          )}
        </aside>
        <div className="search-results">
          {!debounced.trim() && <div className="dock-empty">Type to search. Results stream as you type.</div>}
          {results.data && debounced.trim() && (
            <div className="mono faint" style={{ padding: '4px 12px' }}>
              {results.data.total} results · {results.data.tookMs} ms · {results.data.retrievers.join(' + ')}
            </div>
          )}
          {results.data?.hits.map((h, i) => (
            <div key={h.id} className={`result${i === sel ? ' sel' : ''}`} onMouseEnter={() => setSel(i)} onClick={(e) => openNote(h.id, { split: e.ctrlKey || e.metaKey })}>
              <div className="t">
                <TypeDot type={h.type} />
                <span className="title">{h.title}</span>
                <span className="mono faint path">{h.path}</span>
              </div>
              <div className="snippet" dangerouslySetInnerHTML={{ __html: sanitizeSnippet(h.snippet) }} />
              <div className="why">
                {h.why?.keyword != null && <span className="chip">keyword #{h.why.keyword}</span>}
                {h.why?.vector != null && <span className="chip accent">meaning #{h.why.vector}</span>}
                {h.why?.graph && <span className="chip">linked</span>}
                {h.why?.heading && <span className="faint">{h.why.heading}</span>}
                {h.tags.slice(0, 4).map((t) => <span key={t} className="faint">#{t}</span>)}
              </div>
            </div>
          ))}
        </div>
        <aside className="search-preview">
          {preview.data ? (
            <>
              <div className="title">{preview.data.title}</div>
              <div className="meta">
                <span className="chip accent">{typeLabel(preview.data.type)}</span>
                {preview.data.tags.map((t) => <span key={t} className="chip">#{t}</span>)}
              </div>
              <div className="mono faint" style={{ marginBottom: 8 }}>{preview.data.path}</div>
              <pre className="body">{preview.data.body.slice(0, 4000)}</pre>
              <button className="primary" onClick={() => openNote(preview.data!.id)}>Open</button>
            </>
          ) : (
            <div className="dock-empty">Select a result to preview it.</div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Facet({ title, values, selected, onSelect, label }: { title: string; values: Record<string, number>; selected: string; onSelect: (v: string) => void; label: (v: string) => string }) {
  const entries = Object.entries(values).sort((a, b) => b[1] - a[1]).slice(0, 12);
  if (!entries.length && !selected) return null;
  return (
    <div className="facet">
      <div className="label">{title}{selected && <button className="icon" onClick={() => onSelect('')} title="clear"><X size={11} /></button>}</div>
      {entries.map(([v, n]) => (
        <button key={v} className={`facet-row${selected === v ? ' on' : ''}`} onClick={() => onSelect(selected === v ? '' : v)}>
          <span className="t">{label(v)}</span><span className="mono faint">{n}</span>
        </button>
      ))}
    </div>
  );
}

function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}
